<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue';
import colors from '@/colors.json';
import { useBackendFetch } from '@/fetch';
import { useStore } from '@/stores/main';
import { useIonRouter } from '@ionic/vue';
import { nextTick } from 'vue';

const handleItemPicked = async () => {
  await nextTick();

  const scrollingSections = document.querySelectorAll(
    '.scrolling_down_section',
  );

  if (scrollingSections.length > 0) {
    scrollingSections.forEach(section => {
      section.scrollTo({
        top: section.scrollHeight,
        behavior: 'smooth',
      });
    });
  }
};

const props = defineProps({
  index: Number,
  data: Object,
  name: String,
  colorScheme: String,
  expand: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'picked'): void;
  (e: 'improve', value: string): void;
}>();

const store = useStore();
const router = useIonRouter();

const questionData = inject<any>('questionData');

// Shared state from parent - tracks which answer is being optimistically selected
const optimisticSelectedValue = inject<any>('optimisticSelectedValue', ref(null));

// Optimistic UI state for improve selection
const pendingImproveValue = ref<string | null>(null);
const isImproveLoading = ref(false);

const profileValue = computed(() => store.getProfileValue(props.name || ''));
const profileFactor = computed(() =>
  store.getProfileFactor(questionData?.category_id),
);
const improveValue = computed(() => {
  // Use pending value for instant feedback, fallback to store value
  if (pendingImproveValue.value !== null) {
    return pendingImproveValue.value;
  }
  // Convert to string for consistent comparison (API might return number or string)
  const storeImprove = profileFactor.value?.improve;
  return storeImprove !== undefined && storeImprove !== null ? String(storeImprove) : null;
});
const improveLabel = computed(() => profileFactor.value?.label);
const isChecked = computed(() => profileValue.value);

const borderColor = computed(() => {
  const colorMap: Record<string, string> = {
    purple: 'border-purple-light',
    yellow: 'border-yellow-light',
    green: 'border-green-light',
    blue: 'border-blue-light',
  };
  return props.colorScheme ? colorMap[props.colorScheme] : 'border-yellow-light';
});

const accentColor = computed(() => {
  const colorMap: Record<string, string> = {
    purple: 'bg-purple-light',
    yellow: 'bg-yellow-light',
    green: 'bg-green-light',
    blue: 'bg-blue-light',
  };
  return props.colorScheme ? colorMap[props.colorScheme] : 'bg-yellow-light';
});

// Check if this answer is selected from store (already saved)
const storeSelected = computed(() => isChecked.value == props.data?.value);

// Check if this answer is being optimistically selected
const isOptimisticallySelected = computed(() => optimisticSelectedValue?.value == props.data?.value);

// This answer is selected if: optimistically selected OR (store selected AND nothing else is optimistically selected)
const isSelected = computed(() => {
  if (isOptimisticallySelected.value) return true;
  if (storeSelected.value && optimisticSelectedValue?.value === null) return true;
  return false;
});

// Optimistic model value for the radio - this makes the circle move instantly
const optimisticModelValue = computed(() => {
  // If there's an optimistic selection happening, use that
  if (optimisticSelectedValue?.value !== null) {
    return optimisticSelectedValue?.value;
  }
  // Otherwise use the store value
  return isChecked.value;
});

// Show improve section only for the truly selected answer
const showImproveSection = computed(() => {
  // If this item is optimistically selected, show it
  if (isOptimisticallySelected.value) return true;
  // If store says this is selected AND no optimistic selection is happening, show it
  if (storeSelected.value && optimisticSelectedValue?.value === null) return true;
  return false;
});

const classes = computed(() => [
  'answer-card',
  'list-none',
  'rounded-xl',
  'border-2',
  'border-solid',
  borderColor.value,
  isSelected.value ? 'answer-selected' : 'bg-white',
  'transition-all',
  'duration-200',
]);

// Handle answer selection with optimistic update
const handleAnswerSelect = () => {
  if (optimisticSelectedValue) {
    optimisticSelectedValue.value = props.data?.value;
  }
  handleItemPicked();
  emit('picked');
  // Don't clear optimisticSelectedValue here - it will be overwritten when user clicks a different answer
  // Clearing it causes visual jumping when the store hasn't updated yet
};

// Handle improve selection with optimistic update
const handleImproveClick = async (value: string) => {
  // Optimistic update - show selection immediately
  pendingImproveValue.value = value;
  isImproveLoading.value = true;
  
  // Emit the event (parent will handle API call)
  emit('improve', value);
  
  // Clear pending state after a short delay (API should have responded)
  setTimeout(() => {
    pendingImproveValue.value = null;
    isImproveLoading.value = false;
  }, 1000);
};
</script>

<template>
  <li :class="classes">
    <b-radio
      :name="name"
      :value="data?.value"
      :color="colorScheme"
      :label="data?.label"
      :icon="data?.icon"
      :label-name="`${name}-${index}-label`"
      @change="handleAnswerSelect"
      :model-value="optimisticModelValue"
      class="mx-4 answer-radio"
    />

    <div
      v-if="expand && showImproveSection"
      class="improve-section"
      :class="[accentColor]"
    >
      <div class="improve-options">
        <button
          type="button"
          class="improve-btn"
          :class="{
            'improve-btn-selected': improveValue === '1',
            'improve-btn-satisfied': true,
          }"
          @click="handleImproveClick('1')"
          :disabled="isImproveLoading && pendingImproveValue !== '1'"
        >
          <span class="improve-icon">😊</span>
          <span class="improve-text">Detta är jag nöjd med</span>
        </button>
        <button
          type="button"
          class="improve-btn"
          :class="{
            'improve-btn-selected': improveValue === '2',
            'improve-btn-improve': true,
          }"
          @click="handleImproveClick('2')"
          :disabled="isImproveLoading && pendingImproveValue !== '2'"
        >
          <span class="improve-icon">💪</span>
          <span class="improve-text">Detta vill jag förbättra</span>
        </button>
      </div>
    </div>
  </li>
</template>

<style scoped>
.answer-card {
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 0.5rem;
}

/* Only apply hover effects on non-touch devices */
@media (hover: hover) and (pointer: fine) {
  .answer-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
}

.answer-card:active {
  transform: translateY(0);
}

/* Prevent accidental double-taps on touch devices */
.answer-card {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.answer-selected {
  background: linear-gradient(135deg, #fffbee 0%, #fff3d2 100%);
  border-color: #fdc833;
  box-shadow: 0 4px 16px rgba(253, 200, 51, 0.25);
}

.answer-radio {
  padding: 1rem 0.5rem;
  justify-content: flex-start;
  text-align: left;
}

.improve-section {
  padding: 1rem 1.25rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.improve-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.improve-btn {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 2px solid transparent;
  border-radius: 0.75rem;
  background: white;
  cursor: pointer;
  transition: all 0.15s ease;
  width: 100%;
  text-align: left;
}

/* Only apply hover effects on non-touch devices */
@media (hover: hover) and (pointer: fine) {
  .improve-btn:hover:not(:disabled) {
    transform: scale(1.02);
  }
}

.improve-btn:active:not(:disabled) {
  transform: scale(0.98);
}

/* Prevent accidental double-taps on touch devices */
.improve-btn {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.improve-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

.improve-btn-satisfied {
  border-color: #cbe7a5;
}

.improve-btn-satisfied:hover:not(:disabled) {
  background: #f9fdf2;
  border-color: #72a231;
}

.improve-btn-satisfied.improve-btn-selected {
  background: linear-gradient(135deg, #f9fdf2 0%, #e2ead6 100%);
  border-color: #72a231;
  box-shadow: 0 2px 8px rgba(114, 162, 49, 0.3);
}

.improve-btn-improve {
  border-color: #b0e4dc;
}

.improve-btn-improve:hover:not(:disabled) {
  background: #f3fffe;
  border-color: #2b9984;
}

.improve-btn-improve.improve-btn-selected {
  background: linear-gradient(135deg, #f3fffe 0%, #dfefed 100%);
  border-color: #2b9984;
  box-shadow: 0 2px 8px rgba(43, 153, 132, 0.3);
}

.improve-icon {
  font-size: 1.5rem;
  line-height: 1;
}

.improve-text {
  font-size: 0.95rem;
  font-weight: 500;
  color: #333;
}

.improve-btn-selected .improve-text {
  font-weight: 600;
  color: #111;
}

@media (min-width: 640px) {
  .improve-options {
    flex-direction: row;
  }
  
  .improve-btn {
    flex: 1;
    justify-content: flex-start;
    text-align: left;
    flex-direction: row;
    padding: 0.875rem 1rem;
  }
  
  .improve-icon {
    font-size: 1.5rem;
  }
}
</style>
